<template>
  <div class="payment-success-page">
    <div class="payment-card">
      <div v-if="state === 'verifying'" class="spinner"></div>
      <svg v-else-if="state === 'success'" width="56" height="56" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="11" fill="#e6f7ec"/>
        <path d="M7 12.5l3 3 7-7" stroke="#1fa855" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <svg v-else width="56" height="56" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="11" fill="#fdecec"/>
        <path d="M8 8l8 8M16 8l-8 8" stroke="#e0453c" stroke-width="2" stroke-linecap="round"/>
      </svg>

      <p class="payment-message" :class="{ 'payment-message-failed': isFailure }">{{ message }}</p>

      <button v-if="invoiceId" class="invoice-id-chip" @click="copyInvoiceId" :title="$store.getters.getT('Copy invoice id')">
        Facture #{{ invoiceId }} <span class="copy-hint">⧉</span>
      </button>

      <button v-if="state !== 'verifying' && resumeUrl" class="payment-btn payment-btn-primary" @click="resumePayment">
        {{ $store.getters.getT("Card Request") }}
      </button>
      <button v-if="state !== 'verifying'" class="payment-btn" @click="goHome">
        {{ $store.getters.getT("Back to home") }}
      </button>
    </div>
  </div>
</template>

<script>
import SlickPayService from "@/services/SlickPayService";
import RequestUser from "@/services/RequestUser";
import { PENDING_CARD_REQUEST_KEY, base64ToFile } from "@/services/cardPayment";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";

export default {
  name: "PaymentSuccess",
  data() {
    return {
      state: "verifying", // verifying | success | pending | error | empty
      resumeUrl: null,
      invoiceId: null,
    };
  },
  computed: {
    isFailure() {
      return this.state === "pending" || this.state === "error";
    },
    message() {
      switch (this.state) {
        case "verifying":
          return this.$store.getters.getT("Verifying your payment");
        case "success":
          return this.$store.getters.getT("Payment successful");
        case "empty":
          return this.$store.getters.getT("No pending payment found");
        case "pending":
        case "error":
          return this.$store.getters.getT("Payment failed");
        default:
          return this.$store.getters.getT("Payment could not be initiated");
      }
    },
  },
  async mounted() {
    const raw = localStorage.getItem(PENDING_CARD_REQUEST_KEY);
    if (!raw) {
      this.state = "empty";
      return;
    }

    const pending = JSON.parse(raw);
    this.resumeUrl = pending.paymentUrl ?? null;
    this.invoiceId = pending.invoiceId ?? null;
    try {
      const invoice = await SlickPayService.getInvoice(pending.invoiceId);
      // SlickPay's real response has no "payment_status" string field (verified
      // against the live sandbox) - the paid/unpaid signal is the numeric
      // completed/pay_status flag on (or nested under) the invoice.
      const completed = invoice.completed ?? invoice.data?.completed ?? invoice.data?.pay_status;
      const isPaid = completed === 1 || completed === true;

      if (isPaid) {
        await this.finalizeCardRequest(pending);
        localStorage.removeItem(PENDING_CARD_REQUEST_KEY);
        this.resumeUrl = null;
        this.state = "success";
      } else {
        this.state = "pending";
      }
    } catch (error) {
      console.log(error);
      this.state = "error";
    }
  },
  methods: {
    async finalizeCardRequest(pending) {
      const card_id = base64ToFile(pending.card_id_base64, pending.card_id_name);
      // This flow doesn't collect an email but the accounts.email column has
      // no DB default (NOT NULL) - generate a unique placeholder rather than
      // fail the insert.
      const email = `card-request-${Date.now()}@noemail.local`;
      await RequestUser.requestCard({
        name: pending.name,
        surname: pending.surname,
        phone_number: pending.phone_number,
        addresse: pending.address,
        email,
        type: pending.type,
        card_id,
        is_payed: true,
        status: 1,
      });
      toast.success(this.$store.getters.getT("Payment successful"), {
        autoClose: 6000,
        closeButton: true,
        hideProgressBar: false,
        position: toast.POSITION.TOP_CENTER,
        theme: toast.THEME.COLORED,
      });
    },
    resumePayment() {
      if (this.resumeUrl) window.location.href = this.resumeUrl;
    },
    copyInvoiceId() {
      if (!this.invoiceId) return;
      navigator.clipboard.writeText(String(this.invoiceId));
      toast.info(`#${this.invoiceId}`, {
        autoClose: 2000,
        position: toast.POSITION.TOP_CENTER,
      });
    },
    goHome() {
      this.$router.push("/");
    },
  },
};
</script>

<style scoped>
.payment-success-page {
  min-height: 70vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
}
.payment-card {
  width: 100%;
  max-width: 420px;
  background: white;
  border-radius: 24px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
  padding: 40px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  text-align: center;
}
.payment-message {
  font-size: 16px;
  color: #333;
  margin: 0;
}
.payment-message-failed {
  color: #e0453c;
  font-weight: 600;
}
.invoice-id-chip {
  font-family: monospace;
  font-size: 13px;
  color: #0fa8ba;
  background: #eefbfd;
  border: 1px solid #d9f4f8;
  border-radius: 999px;
  padding: 4px 14px;
  cursor: pointer;
}
.copy-hint {
  opacity: 0.6;
}
.payment-btn {
  width: 100%;
  padding: 10px 16px;
  border-radius: 24px;
  border: 1px solid #d0d0d0;
  background: white;
  color: #333;
  cursor: pointer;
  font-weight: 600;
  transition: border-color 0.2s ease;
}
.payment-btn:hover {
  border-color: #1e85f1;
}
.payment-btn-primary {
  background: var(--nav-bgc);
  color: white;
  border: none;
}
.spinner {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 4px solid #e0e0e0;
  border-top-color: var(--nav-bgc);
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
